import { Switch } from '@repo/design-system/index'
import { QIcon_download, QIcon_headphone } from '@repo/icons/index'
import classNames from 'classnames'
import { differenceInMinutes } from 'date-fns'
import { Dispatch, Fragment, SetStateAction, useEffect, useRef, useState } from 'react'
import LoadingSpinner from '../../../components/common/LoadingSpinner'
import { SCRIPT_STATUS } from '../../../model/script.interface'
import apis from '../../../modules/apis'
import { useScriptStatusV5 } from '../../../modules/apis/student/lesson'
import { axiosPost } from '../../../modules/axiosHelper'
import { n_lesson_review, n_review, s_lesson_review, s_review } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { MIC_GRAY } from '../../../modules/images'
import { ICON_20PX_NOTICE, ICON_LOGO_WHITE_ROUND } from '../../../modules/svg/svg'
import { COLORS } from '../../../modules/vars'
import Button from '../../_legacy/Button'

interface DialogType {
  content: string
  end_time: string
  formatted_end_time_to_milliseconds: number
  formatted_time: string
  formatted_time_to_milliseconds: number
  id: number
  image_url: string
  role: number
  start_time: string
}

interface DialogBlockType {
  dialog: DialogType
  isActive: boolean
  onDialogClick: (time: number) => void
}

const DialogTime = ({ time }: { time: any }) => (
  <span className="text-sody mx-[8px] flex items-end text-[12px] text-gray-300">{time}</span>
)

const DialogBlock = ({ dialog, isActive, onDialogClick }: DialogBlockType) => {
  return (
    <Fragment>
      {dialog.role ? (
        <div
          onClick={() => onDialogClick(dialog.formatted_time_to_milliseconds)}
          style={!isActive ? { opacity: 0.8 } : { opacity: 1 }}
          className={classNames([isActive && 'bg-gray-100'], 'flex cursor-pointer py-[8px]')}
        >
          <img src={dialog.image_url} className="border-1 ml-[16px] h-[38px] w-[38px] rounded-full border-gray-200" />
          <span className="border-1 ml-[16px] max-w-[400px] rounded-[4px] border-gray-200 bg-white px-[12px] py-[8px] text-[14px] text-gray-700 shadow-md">
            {dialog.content}
          </span>
          <DialogTime time={dialog.formatted_time} />
        </div>
      ) : (
        <div
          style={!isActive ? { opacity: 0.8 } : { opacity: 1 }}
          onClick={() => onDialogClick(dialog.formatted_time_to_milliseconds)}
          className={classNames(
            [isActive && 'bg-gray-100'],
            'mr-[16px] flex cursor-pointer justify-end bg-gray-50 py-[8px]'
          )}
        >
          <DialogTime time={dialog.formatted_time} />
          <span className="max-w-[400px] rounded-[4px] bg-purple-500 px-[12px] py-[8px] text-[14px] text-white">
            {dialog.content}
          </span>
        </div>
      )}
    </Fragment>
  )
}

type RecordedScriptPropsType = {
  script: any
  lesson: any
  audioCurrentTime: number
  setAudioCurrentTime: Dispatch<SetStateAction<number>>
  setIsAudioAuto?: Dispatch<SetStateAction<boolean>>
}

const RecordedScript = ({
  script,
  lesson,
  audioCurrentTime,
  setAudioCurrentTime,
  setIsAudioAuto,
}: RecordedScriptPropsType) => {
  const { t } = useTranslation()
  const [isAutoScroll, setIsAutoScroll] = useState(true)

  const { data: status } = useScriptStatusV5()

  const dialogContainerRef = useRef(null)
  const chatContainerRef = useRef(null)

  const NOT_READY_PLACEHOLDER_MAP = {
    [SCRIPT_STATUS.NOSHOW]: t(n_review.noshow_audio), //노쇼
    [SCRIPT_STATUS.IN_PROGRESS]: t(n_lesson_review.script_status_title1), //준비중
    [SCRIPT_STATUS.RECORD_NOT_ALLOWED]: t(n_lesson_review.script_status_title2), // 리포트 제공 X
  }

  const NOT_READY_SUB_PLACEHOLDER_MAP = {
    [SCRIPT_STATUS.NOSHOW]: '',
    [SCRIPT_STATUS.IN_PROGRESS]: t(n_lesson_review.script_status_detail1),
    [SCRIPT_STATUS.RECORD_NOT_ALLOWED]: t(n_lesson_review.script_status_detail2),
  }

  useEffect(() => {
    if (isAutoScroll) scrollMove()
  }, [audioCurrentTime])

  const scrollMove = () => {
    if (chatContainerRef.current && dialogContainerRef.current) {
      dialogContainerRef.current.scrollTo({
        top: chatContainerRef.current.offsetTop - dialogContainerRef.current.clientHeight / 2,
        behavior: 'smooth',
      })
    }
  }

  const isActive = (dialog, index) => {
    return (
      index < script.dialog_array.length - 1 &&
      dialog.formatted_time_to_milliseconds <= audioCurrentTime &&
      (audioCurrentTime < script.dialog_array[index + 1].formatted_time_to_milliseconds ||
        audioCurrentTime < dialog.formatted_end_time_to_milliseconds)
    )
  }

  const handleDialogClick = (time) => {
    setIsAudioAuto(false)
    setAudioCurrentTime(time)
  }

  const [inquirySent, setInquirySent] = useState(false)

  const start_time = new Date(lesson?.normal_start_time)
  const now = new Date()
  const diff_time = differenceInMinutes(now, start_time) / 60

  useEffect(() => {
    setInquirySent(script?.request_script)
  }, [script])

  const handleInquiry = async () => {
    if (!inquirySent) {
      const res = await axiosPost(apis.lessons.review.late(lesson.id), {})
      if (res.success) {
        setInquirySent(true)
      } else {
        alert('Fail')
      }
    }
  }

  if (status !== SCRIPT_STATUS.READY) {
    return (
      <div className="mx-auto w-full px-0 sm:w-[640px] lg:w-[800px] xl:w-[776px] ">
        <div className="justify-content flex w-full flex-col items-center bg-white py-[120px] px-[20px] sm:px-[0px]">
          <img src={MIC_GRAY} width={64} height={64} />
          <div className="mt-[20px]  flex flex-col items-center text-[18px] font-bold sm:text-[20px]">
            {t(NOT_READY_PLACEHOLDER_MAP[status])}
          </div>
          <div className="mt-[12px] text-[16px] text-gray-500 sm:text-[20px]">
            {t(NOT_READY_SUB_PLACEHOLDER_MAP[status])}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="sm:border-l-1 relative flex h-full flex-col">
      <div className="sm:border-b-1 sticky top-0 z-[500] border-b-0 border-gray-200 bg-white pl-[16px]">
        <AutoScroll
          title={t(n_lesson_review.script)}
          isScript={script?.dialog_array?.length > 0}
          description={t(s_review.recorded_script)}
          downloadLink={script?.url}
          tutorAudioDownloadLink={script?.tutor_audio_url}
          isAutoScroll={isAutoScroll}
          setIsAutoScroll={setIsAutoScroll}
        />
      </div>
      <div
        ref={dialogContainerRef}
        style={{ height: 'calc(100vh - 180px)' }}
        className="h-full overflow-y-scroll bg-white sm:bg-gray-50"
      >
        <div>
          {script ? (
            script.dialog_array?.length > 0 ? (
              <div className="flex flex-col pb-[60px]">
                <div className="mt-0 flex cursor-pointer py-[8px] sm:mt-[16px]">
                  <img src={ICON_LOGO_WHITE_ROUND} className="ml-[16px] h-[38px] w-[38px] rounded-full" />
                  <div className="border-1 ml-[16px] flex max-w-[75%] rounded-[4px] border-gray-200 bg-white px-[12px] py-[8px] text-[14px] text-purple-500">
                    <img src={ICON_20PX_NOTICE} className="h-[20px] w-[20px]" />
                    <span className="ml-[4px] text-purple-500">{t(s_lesson_review.options.record.desc)}</span>
                  </div>
                </div>
                {script.dialog_array.map((dialog: DialogType, index) => (
                  <div ref={isActive(dialog, index) ? chatContainerRef : null} key={dialog.id}>
                    <DialogBlock dialog={dialog} onDialogClick={handleDialogClick} isActive={isActive(dialog, index)} />
                  </div>
                ))}
                <div className="h-[60px]"></div>
              </div>
            ) : (
              <div
                style={{
                  height: 'calc(100vh - 250px)',
                }}
                className="mt-[64px] flex flex-col items-center justify-center px-[40px]"
              >
                <span className="fe fe-alert-circle mb-[16px] text-red-500"></span>
                <div className="text-h5 text-center">
                  {lesson?.script_permission === false || lesson?.allow_record === false ? (
                    t(s_review.script_you_can_setting_record)
                  ) : lesson?.status?.err ? (
                    t(s_review.noshow)
                  ) : diff_time < 24 ? (
                    t(s_review.script_being_prepared)
                  ) : (
                    <div>
                      {t(s_review.script_being_delayed)}
                      <br />
                      <br />
                      {inquirySent ? (
                        <div>{t(s_review.successfully_submitted)}</div>
                      ) : (
                        <Button sm primary onClick={handleInquiry}>
                          {t(s_review.inquiry)}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          ) : (
            <LoadingSpinner show primary lg muted />
          )}
        </div>
      </div>
    </div>
  )
}

const AutoScroll = ({
  title,
  description,
  isScript = false,
  downloadLink,
  tutorAudioDownloadLink,
  isAutoScroll,
  setIsAutoScroll,
}) => {
  const downloadRef = useRef(null)
  const [showDownloadTooltip, setShowDownloadTooltip] = useState<boolean>(false)

  const handleToggleChange = () => {
    setIsAutoScroll((prev) => !prev)
  }

  useEffect(() => {
    function handleClickOutside(event) {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setShowDownloadTooltip(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [downloadRef])

  return (
    <div className="mb-[3px] flex w-full flex-row items-center py-[12px] sm:h-[62px] sm:py-0">
      <span className="text-h2 hidden font-bold text-black sm:block">{title}</span>
      {isScript && (
        <div className="flex flex-auto justify-end">
          <div className="mr-[8px] text-[14px] text-gray-500">Auto Scroll</div>
          <div className="relative top-[2px] mr-[16px]">
            <Switch checked={isAutoScroll} setChecked={handleToggleChange} />
          </div>
        </div>
      )}

      {(downloadLink || tutorAudioDownloadLink) && (
        <div className="relative mr-[24px] hidden sm:block" ref={downloadRef}>
          <button
            className="rounded-[7px] border-[1.5px] py-[11px] px-[10px]"
            onClick={() => setShowDownloadTooltip((prev) => !prev)}
          >
            <QIcon_download width={24} height={24} color={COLORS.BLACK} />
          </button>
          {showDownloadTooltip && (
            <div className="speech-bubble-download absolute right-[50%] top-[60px] z-[500] w-[90px] translate-x-2/4 rounded-[5px] bg-white shadow-md">
              <div className="border-1 rounded-[5px]  py-[8px]">
                <div className="flex flex-col items-center justify-center gap-[20px]">
                  <div className="flex flex-col items-center justify-center">
                    <a
                      rel="noreferrer"
                      {...(downloadLink ? { href: downloadLink, rel: 'noreferrer' } : {})}
                      className={`Caption-12Md flex flex-col items-center justify-center
                      ${!downloadLink ? 'opacity-50 pointer-events-none cursor-not-allowed' : ''}
                      `}
                    >
                      <div className=" flex items-center justify-center rounded-full bg-gray-100 p-[12px] text-gray-700">
                        <QIcon_headphone />
                      </div>
                      Merged MP3
                    </a>
                  </div>
                  <div className="flex flex-col items-center justify-center" style={{ width: '100%' }}>
                    <a
                      rel="noreferrer"
                      {...(tutorAudioDownloadLink ? { href: tutorAudioDownloadLink, rel: 'noreferrer' } : {})}
                      className={`Caption-12Md flex flex-col items-center justify-center
                      ${!tutorAudioDownloadLink ? 'opacity-50 pointer-events-none cursor-not-allowed' : ''}
                      `}
                    >
                      <div className=" flex items-center justify-center rounded-full bg-gray-100 p-[12px] text-gray-700">
                        <QIcon_headphone />
                      </div>
                      Tutor MP3
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
export default RecordedScript
